import { useState } from "react";
import { Scale, Copy, NotebookPen, Trash2, ShieldAlert, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Card, EmptyState, Field, Tag, ErrorBox, Spinner, ConfirmDialog } from "@/components/lf";
import FollowUp from "@/components/FollowUp";
import { useStore, useAsync, fmtDate } from "@/lib/hooks";
import { add, update, remove, StorageError } from "@/lib/storage";
import { fetchAnalysis, aiStatus } from "@/lib/api";
import { useEffect } from "react";

export const analysisToText = (a) => {
  const r = a.result;
  const L = (t, xs) => (xs && xs.length ? `\n${t}:\n${xs.map((x) => `- ${x}`).join("\n")}\n` : "");
  const F = (xs) => xs.map((x) => `${x.nama ? x.nama + ": " : ""}${x.penjelasan} [${x.status === "kesalahan_terbukti" ? "Kesalahan terbukti" : "Kemungkinan kelemahan"}]`);
  return [
    `ANALISIS ARGUMEN — Logika Filsuf`,
    `Argumen asli: ${a.input.argument}`,
    `\nKlaim utama: ${r.ringkasan_klaim}`,
    L("Premis pendukung", r.premis_pendukung), L("Bukti tersedia", r.bukti_tersedia), L("Asumsi tersembunyi", r.asumsi_tersembunyi),
    L("Kelemahan logis", F(r.kelemahan_logis)), L("Potensi kekeliruan", F(r.potensi_kekeliruan)),
    L("Bantahan", r.bantahan), L("Pertanyaan kritis", r.pertanyaan_kritis),
    `\nVersi lebih jelas:\n${r.versi_lebih_jelas}`,
    `\n(Dihasilkan AI — dapat keliru.)`,
  ].join("\n");
};

const Block = ({ title, items, testid }) =>
  items && items.length > 0 ? (
    <div className="mb-6" data-testid={testid}>
      <div className="lf-overline mb-2">{title}</div>
      <ul className="space-y-2 text-sm leading-relaxed list-disc pl-5 marker:text-[#545B6B]">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
    </div>
  ) : null;

const Flaws = ({ title, items, testid }) =>
  items && items.length > 0 ? (
    <div className="mb-6" data-testid={testid}>
      <div className="lf-overline mb-2">{title}</div>
      <div className="space-y-2">
        {items.map((x, i) => (
          <div key={i} className="border border-[#29292D] bg-[#1B1B1F] rounded-sm p-3.5">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              {x.nama && <span className="font-serif text-lg">{x.nama}</span>}
              {x.status === "kesalahan_terbukti" ? <Tag tone="red"><ShieldAlert size={11} className="mr-1" />Kesalahan terbukti</Tag> : <Tag tone="warn"><HelpCircle size={11} className="mr-1" />Kemungkinan kelemahan</Tag>}
            </div>
            <p className="text-sm text-[#F2F0EC]/85 leading-relaxed">{x.penjelasan}</p>
          </div>
        ))}
      </div>
    </div>
  ) : null;

function Result({ a, onNote, onCopy, onDelete }) {
  const r = a.result;
  return (
    <Card className="lf-fade" data-testid="analysis-result">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div><div className="lf-overline mb-1">Klaim utama</div><p className="font-serif text-2xl leading-snug">{r.ringkasan_klaim}</p></div>
        <Tag>{a.input.depth}</Tag>
      </div>
      <Block title="Premis pendukung" items={r.premis_pendukung} testid="an-premises" />
      <Block title="Bukti yang tersedia" items={r.bukti_tersedia} testid="an-evidence" />
      <Block title="Asumsi tersembunyi" items={r.asumsi_tersembunyi} testid="an-assumptions" />
      <Flaws title="Kelemahan logis" items={r.kelemahan_logis} testid="an-weaknesses" />
      <Flaws title="Potensi kekeliruan (fallacy)" items={r.potensi_kekeliruan} testid="an-fallacies" />
      <Block title="Bantahan yang mungkin" items={r.bantahan} testid="an-counter" />
      <Block title="Pertanyaan kritis lanjutan" items={r.pertanyaan_kritis} testid="an-questions" />
      {r.versi_lebih_jelas && (
        <div className="mb-6 border-l-2 border-[#E6E2D6] pl-4" data-testid="an-clearer">
          <div className="lf-overline mb-2">Versi argumen yang lebih jelas</div>
          <p className="font-serif text-xl leading-relaxed italic">{r.versi_lebih_jelas}</p>
        </div>
      )}
      <p className="text-xs text-[#6b6a70] mb-4">Dihasilkan AI ({r.model}). “Kesalahan terbukti” = dapat ditunjukkan dari teks; “kemungkinan kelemahan” = bergantung konteks. Selalu nilai sendiri.</p>
      <div className="flex flex-wrap gap-2">
        <button data-testid="analysis-save-note-button" onClick={onNote} disabled={!!a.noteId} className="lf-btn !py-2"><NotebookPen size={14} />{a.noteId ? "Sudah disimpan ke catatan" : "Simpan ke catatan"}</button>
        <button data-testid="analysis-copy-button" onClick={onCopy} className="lf-btn-ghost !py-2"><Copy size={14} />Salin analisis</button>
        <button data-testid="analysis-delete-button" onClick={onDelete} className="lf-btn-ghost !py-2 !text-[#d98a8a]"><Trash2 size={14} />Hapus</button>
      </div>
      <FollowUp type="analisis" contextText={`Argumen: ${a.input.argument}\nKlaim utama: ${r.ringkasan_klaim}\nAsumsi: ${(r.asumsi_tersembunyi || []).join("; ")}`} />
    </Card>
  );
}

export default function BedahArgumen() {
  const history = useStore("argument_analyses");
  const [form, setForm] = useState({ argument: "", context: "", depth: "Standar" });
  const [activeId, setActiveId] = useState(null);
  const [del, setDel] = useState(null);
  const [status, setStatus] = useState(null);
  const [st, run] = useAsync();
  useEffect(() => { aiStatus().then(setStatus); }, []);

  const active = history.find((h) => h.id === activeId) || history[0] || null;
  const unconfigured = status && !status.configured;

  const submit = async (e) => {
    e.preventDefault();
    if (form.argument.trim().length < 10) return toast.error("Tuliskan argumen minimal 10 karakter.");
    const res = await run(() => fetchAnalysis({ argument: form.argument.trim(), context: form.context.trim(), depth: form.depth }));
    if (!res) return;
    try {
      const rec = add("argument_analyses", { input: { ...form, argument: form.argument.trim() }, result: res, noteId: null });
      setActiveId(rec.id);
      toast.success("Analisis selesai dan tersimpan di riwayat.");
    } catch (err) { toast.error(err instanceof StorageError ? err.message : "Gagal menyimpan."); }
  };

  const toNote = (a) => {
    try {
      const n = add("notes", {
        title: `Analisis: ${a.result.ringkasan_klaim.slice(0, 70)}`, content: analysisToText(a), quote: a.input.argument.slice(0, 300),
        bookId: "", tags: ["analisis-argumen"], link: { type: "analysis", id: a.id },
      });
      update("argument_analyses", a.id, { noteId: n.id });
      toast.success("Analisis disimpan ke Catatan Saya.");
    } catch (err) { toast.error(err.message); }
  };

  const copy = async (a) => {
    try { await navigator.clipboard.writeText(analysisToText(a)); toast.success("Analisis disalin."); }
    catch { toast.error("Tidak dapat menyalin otomatis. Pilih teks secara manual."); }
  };

  return (
    <div>
      <PageHeader testid="page-title-bedah" overline="Ruang kerja" title="Bedah Argumen" subtitle="Tulis klaim atau paragraf. Kami akan memetakan premis, asumsi, kelemahan, dan bantahan, dengan membedakan kesalahan terbukti dari kemungkinan kelemahan." />
      {unconfigured && <div data-testid="ai-config-required" className="border border-[#7a5c2e] bg-[#1c1710] text-[#d4a95f] text-sm rounded-sm px-4 py-3 mb-6">Konfigurasi diperlukan: layanan AI belum dikonfigurasi di server.</div>}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-8">
        <div className="xl:col-span-2 space-y-6">
          <form onSubmit={submit} className="lf-card p-6 space-y-5" data-testid="analysis-form">
            <Field label="Argumen / klaim / paragraf" hint={`${form.argument.length}/4000`}>
              <textarea data-testid="analysis-argument-input" rows={8} maxLength={4000} value={form.argument} onChange={(e) => setForm({ ...form, argument: e.target.value })} placeholder="Mis. Semua orang kaya pasti bekerja keras, jadi orang miskin pasti malas." className="lf-field" />
            </Field>
            <Field label="Konteks (opsional)"><textarea data-testid="analysis-context-input" rows={2} maxLength={1500} value={form.context} onChange={(e) => setForm({ ...form, context: e.target.value })} className="lf-field" /></Field>
            <Field label="Kedalaman analisis"><select data-testid="analysis-depth-select" className="lf-field" value={form.depth} onChange={(e) => setForm({ ...form, depth: e.target.value })}>{["Ringkas", "Standar", "Mendalam"].map((x) => <option key={x}>{x}</option>)}</select></Field>
            <button data-testid="analysis-submit-button" disabled={st.loading || unconfigured} className="lf-btn w-full !py-3">{st.loading ? <Spinner label="Kami akan membedah argumen Anda…" /> : <><Scale size={16} />Bedah argumen</>}</button>
            <ErrorBox error={st.error} testid="analysis-error" />
          </form>

          <div data-testid="analysis-history">
            <div className="lf-overline mb-3">Riwayat analisis ({history.length})</div>
            {history.length === 0 ? <EmptyState testid="empty-analysis-history" title="Belum ada riwayat" text="Setiap analisis tersimpan otomatis di sini." /> : (
              <ul className="border border-[#29292D] rounded-md divide-y divide-[#29292D]">
                {history.map((h) => (
                  <li key={h.id} data-testid="analysis-history-item" className={`flex items-start gap-2 p-3.5 bg-[#151518] ${active?.id === h.id ? "border-l-2 border-[#E6E2D6]" : ""}`}>
                    <button className="flex-1 text-left" onClick={() => setActiveId(h.id)} data-testid="analysis-history-open">
                      <div className="text-sm line-clamp-2">{h.input.argument}</div>
                      <div className="text-xs text-[#929197] mt-1">{fmtDate(h.createdAt)} · {h.input.depth}</div>
                    </button>
                    <button aria-label="Hapus analisis" data-testid="analysis-history-delete" onClick={() => setDel(h)} className="text-[#929197] hover:text-[#d98a8a] p-1"><Trash2 size={15} /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="xl:col-span-3">
          {active ? <Result a={active} onNote={() => toNote(active)} onCopy={() => copy(active)} onDelete={() => setDel(active)} /> : <EmptyState testid="empty-analysis" title="Hasil analisis akan tampil di sini" text="Masukkan argumen lalu tekan “Bedah argumen”." />}
        </div>
      </div>
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Hapus analisis ini?" description="Analisis akan dihapus dari riwayat. Catatan yang sudah dibuat dari analisis ini tidak ikut terhapus." onConfirm={() => { remove("argument_analyses", del.id); if (activeId === del.id) setActiveId(null); setDel(null); toast.success("Analisis dihapus."); }} />
    </div>
  );
}
